import React, { useRef } from 'react';

import { Splide, SplideSlide } from '@splidejs/react-splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';
import styled, { useTheme } from 'styled-components';

import TickerEntry from 'components/PriceTicker/components/TickerEntry';

import useCaxyLightstream from 'hooks/useCaxyLightstream';

import { epicToLabel, tickerEpics } from 'utils/forexTableData';

const PriceTickerWrapper = styled.div`
  height: 2.9rem;
  position: relative;
  cursor: grab;
  border-top: 1px solid ${({ theme }) => theme.colors.secondaryGray2};
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondaryGray2};
`;

const OverflowMask = styled.div`
  overflow: hidden;
`;

const PriceTicker: React.FC = () => {
  const splideRef = useRef<Splide | null>(null);
  const theme = useTheme();
  const { data } = useCaxyLightstream(tickerEpics);

  return (
    <PriceTickerWrapper data-elastic-exclude={true}>
      <OverflowMask>
        <Splide
          ref={splideRef}
          extensions={{ AutoScroll }}
          options={{
            label: 'Price Ticker',
            updateOnMove: true,
            type: 'loop',
            drag: 'free',
            arrows: false,
            fixedWidth: '32rem',
            autoWidth: false,
            focus: 0,
            gap: '2rem',
            padding: { left: '0rem', right: '3.5rem' },
            pagination: false,
            perPage: 4,
            autoScroll: {
              pauseOnHover: true,
              pauseOnFocus: false,
              rewind: false,
              speed: 0.66,
            },
            breakpoints: {
              1920: {
                perPage: 4,
                gap: '2rem',
              },
              1440: {
                perPage: 3.5,
                gap: '2rem',
              },
              1280: {
                perPage: 3,
                gap: '2rem',
              },
              [theme.breakpoints.values.lg]: {
                perPage: 2,
                autoScroll: true,
              },
              [theme.breakpoints.values.md]: {
                perPage: 1,
                autoScroll: true,
              },
              [theme.breakpoints.values.xs]: {
                perPage: 1,
              },
            },
          }}
        >
          {tickerEpics.map((epic, index) => {
            return (
              <SplideSlide key={index}>
                <TickerEntry
                  currency={epicToLabel(epic)}
                  type={data.MARKET[epic]?.UPDATE_TYPE.value}
                  sell={data.MARKET[epic]?.BID.value}
                  sellUp={data.MARKET[epic]?.BID?.change !== -1}
                  buy={data.MARKET[epic]?.OFR.value}
                  buyUp={data.MARKET[epic]?.OFR?.change !== -1}
                  change={data.MARKET[epic]?.CPT.value}
                />
              </SplideSlide>
            );
          })}
        </Splide>
      </OverflowMask>
    </PriceTickerWrapper>
  );
};

export default PriceTicker;
